import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Loader, fadeFromLeft, fadeIn } from "@breward/ui";

import { useApiClient } from "@brewardapp/api-client"
import { useStore } from "@breward/store";
import SharedLoginForm from "../../components/LoginForm";
import RegisterForm from "../../components/RegisterForm";
import ForgottenPasswordForm from "../../components/ForgottenPasswordForm";
import ResetPasswordForm from "../../components/ResetPasswordForm";
// @ts-ignore
import Logo from "../../assets/logo.svg?react"
import BackgroundImage from "../../assets/mppd2023_2.jpg";
import { getSafeRedirectPath, redirectToParam } from "./redirect";

interface SharedLoginPageProps {
    defaultForm?: string;
    redirectPath: string;
}

interface ApiVersionData {
    version?: string;
    environment?: string;
}

const SharedLoginPage: React.FC<SharedLoginPageProps> = ({
    defaultForm = "LOGIN",
    redirectPath
}) => {
    const api = useApiClient()
    const [currentForm, setCurrentForm] = useState(defaultForm);
    const [t] = useTranslation();
    const [searchParams] = useSearchParams();
    const setBackToUrl = useStore((state) => state.setBackToUrl);
    const redirectFromQuery = getSafeRedirectPath(searchParams.get(redirectToParam));

    useEffect(() => {
        if (redirectFromQuery) {
            setBackToUrl?.(redirectFromQuery);
        }
    }, [redirectFromQuery, setBackToUrl]);

    const { data: apiVersionData, isLoading } = api.version.useGetApiVersion({});

    if (isLoading) {
        return <Loader />;
    }

    // Type assertion to ensure apiVersionData is treated as ApiVersionData
    const versionData = apiVersionData as ApiVersionData;

    return (
        <>
            <main className="flex items-center justify-center min-h-screen w-full bg-base-50 dark:bg-content-900">
                <div
                    className="w-full h-screen md:h-[95vh] lg:h-[95vh] lg:max-w-[95vw] relative grid grid-cols-12 gap-8 overflow-hidden shadow-lg shadow-secondary-500/20"
                >
                    <div className="bg-secondary-500 absolute h-full w-full" />
                    <img
                        src={BackgroundImage}
                        className="absolute h-full w-full object-cover opacity-50 grayscale transition-opacity duration-700"
                    />
                    <div className="col-span-12 lg:col-span-5">
                        <div
                            className="h-full bg-base-200 dark:bg-base-950 relative z-50 flex flex-col justify-between"
                        >
                            <div className="flex flex-row items-center justify-center px-4 pb-4 pt-6">
                                <Logo className="text-content-700 h-48 w-48" />
                            </div>
                            <div className="flex-grow overflow-y-auto px-6 md:px-12 lg:px-16 pb-8 pt-8">
                                {currentForm === "LOGIN" && <SharedLoginForm redirectPath={redirectFromQuery || redirectPath} />}
                                {currentForm === "REGISTER" && <RegisterForm redirectPath={redirectFromQuery || redirectPath} />}
                                {currentForm === "FORGOTTEN_PASSWORD" && (
                                    <ForgottenPasswordForm onSuccess={() => setCurrentForm("LOGIN")} />
                                )}
                                {currentForm === "PASSWORD_RESET" && (
                                    <ResetPasswordForm onSuccess={() => setCurrentForm("LOGIN")} />
                                )}
                                <div className="bg-primary-500 mb-6 mt-6 h-0.5 w-full rounded-xl" />
                                <div className="flex flex-row items-center justify-between">
                                    {currentForm === "LOGIN" ? (
                                        <>
                                            <div
                                                onClick={() => setCurrentForm("FORGOTTEN_PASSWORD")}
                                                className="text-md text-primary-600 dark:text-primary-400 cursor-pointer font-sans font-light hover:underline"
                                            >
                                                {t("global:shared.forgottenPasswordLink", "Forgot your password?")}
                                            </div>
                                            <div
                                                data-test-id="switch-to-register-link"
                                                onClick={() => setCurrentForm("REGISTER")}
                                                className="text-md text-primary-600 dark:text-primary-400 cursor-pointer font-sans font-light hover:underline"
                                            >
                                                {t("global:shared.createAccount", "Don't have account yet?")}
                                            </div>
                                        </>
                                    ) : (
                                        <div
                                            onClick={() => setCurrentForm("LOGIN")}
                                            className="text-primary-600 dark:text-primary-400 cursor-pointer font-sans font-light hover:underline"
                                        >
                                            {t("global:shared.backToLogin", "Go back to login")}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="fixed bottom-1 right-5 flex flex-col items-center">
                    <div className="flex flex-row space-x-2">
                        <div className="text-primary-400 font-sans text-[7pt] opacity-50">
                            APP:
                        </div>
                        <div className="text-primary-400 font-sans text-[7pt] font-medium opacity-50">
                            {import.meta.env.APP_VERSION}/{import.meta.env.DEV ? "dev" : "prod"}
                        </div>
                    </div>
                    <div className="flex flex-row space-x-2">
                        <div className="text-primary-400 font-sans text-[7pt] opacity-50">
                            API:
                        </div>
                        <div className="text-primary-400 font-sans text-[7pt] font-medium opacity-50">
                            {versionData?.version}/{versionData?.environment}
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default SharedLoginPage;
